import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { validateImage } from '../lib/imageQuality';
import { analyzeFrame, computeQualityStatus, captureVideoFrame } from '../lib/realtimeQuality';
import { Upload, Camera, Check, X, AlertTriangle, Lock, RefreshCw } from 'lucide-react';
import { LogoMark } from '../components/Logo';

const STEP_DETAILS = 'details';
const STEP_DOC = 'document';
const STEP_DOC_BACK = 'document_back';
const STEP_SELFIE = 'selfie';
const STEP_REVIEW = 'review';
const STEP_DONE = 'done';
const STEP_RETRY_DOC = 'retry_document';

// Camera capture modal. Streams getUserMedia into a <video>, lets the user
// snap a frame, converts it to an in-memory JPEG Blob and hands it off to
// the parent. The stream is fully torn down on close so no audio/video
// continues running, in line with the privacy-first design.
// 
// NEW: Real-time image quality feedback while capturing, with live analysis
// of blur, brightness, and face detection (for selfies).
function CameraModal({ open, onClose, onCapture, facingMode = 'environment', label, testid, captureFileStem, qualityContext = 'image' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [snapshot, setSnapshot] = useState(null); // dataURL preview
  const [error, setError] = useState(null);
  
  // Real-time quality feedback
  const [qualityFeedback, setQualityFeedback] = useState(null);
  const [canCapture, setCanCapture] = useState(false);
  const analysisIntervalRef = useRef(null);
  const isSelfie = label.toLowerCase().includes('selfie');

  const stop = useCallback(() => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
    }
  }, [stream]);

  const start = useCallback(async () => {
    setError(null); setSnapshot(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => {});
        
        // Start real-time quality analysis (every 300ms)
        if (analysisIntervalRef.current) {
          clearInterval(analysisIntervalRef.current);
        }
        analysisIntervalRef.current = setInterval(() => {
          if (!videoRef.current || !videoRef.current.videoWidth) return;
          
          const canvas = captureVideoFrame(videoRef.current);
          if (!canvas) return;
          
          const analysis = analyzeFrame(canvas, isSelfie);
          const status = computeQualityStatus(analysis, isSelfie, qualityContext);
          setQualityFeedback(status);
          setCanCapture(isSelfie && analysis.face?.detected === false ? true : status.canCapture);
        }, 300);
      }
    } catch (e) {
      setError(
        e?.name === 'NotAllowedError'
          ? 'Camera permission denied. Use Upload instead.'
          : 'Could not start camera. Use Upload instead.'
      );
    }
  }, [facingMode, isSelfie, qualityContext]);

  useEffect(() => {
    if (open) start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const snap = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    setSnapshot(c.toDataURL('image/jpeg', 0.92));
  };

  const accept = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const stem = captureFileStem
        || (label.toLowerCase().includes('selfie') ? 'selfie' : 'id_document_front');
      const file = new File([blob], `${stem}.jpg`, { type: 'image/jpeg' });
      stop();
      onCapture(file);
      onClose();
    }, 'image/jpeg', 0.92);
  };

  const retake = () => setSnapshot(null);

  // Get status indicator color
  const getStatusColor = () => {
    if (!qualityFeedback) return 'bg-zinc-200';
    if (qualityFeedback.status === 'good') return 'bg-green-500';
    if (qualityFeedback.status === 'caution') return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose} data-testid={`${testid}-modal`}>
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md p-4" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Camera · {label}</p>
          <button onClick={() => { stop(); onClose(); }} aria-label="Close" data-testid={`${testid}-close`}
            className="w-7 h-7 grid place-items-center text-zinc-500 hover:text-black border border-zinc-200 rounded-sm">
            <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
          </button>
        </div>
        <div className="relative mt-3 aspect-[4/3] bg-black rounded-sm overflow-hidden">
          {snapshot ? (
            <img src={snapshot} alt="snapshot" className="w-full h-full object-cover"/>
          ) : (
            <video ref={videoRef} playsInline muted autoPlay
              className="w-full h-full object-cover"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}/>
          )}
          <canvas ref={canvasRef} className="hidden"/>
          {error && (
            <div className="absolute inset-0 grid place-items-center text-center px-6 text-xs text-white bg-black/80" data-testid={`${testid}-error`}>
              {error}
            </div>
          )}
          {/* Quality feedback overlay */}
          {!snapshot && qualityFeedback && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white px-3 py-2">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor()}`} data-testid={`${testid}-quality-indicator`}/>
                <p className="text-xs font-medium">{qualityFeedback.message}</p>
              </div>
              {qualityFeedback.status !== 'good' && (
                <p className="text-[11px] text-zinc-300">
                  {isSelfie && qualityFeedback.message === 'No face detected'
                    ? 'Face not clearly detected. You can still continue, but make sure your face is visible.'
                    : 'Move closer or adjust lighting'}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          {!snapshot ? (
            <button onClick={snap} disabled={!stream || !!error || !canCapture} data-testid={`${testid}-snap`}
              className="flex-1 bg-black text-white py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <Camera className="w-4 h-4" strokeWidth={1.5}/> {canCapture ? 'Capture' : 'Waiting...'}
            </button>
          ) : (
            <>
              <button onClick={retake} data-testid={`${testid}-retake`}
                className="flex-1 border border-zinc-200 py-2.5 rounded-sm text-sm hover:border-zinc-400 inline-flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4" strokeWidth={1.5}/> Retake
              </button>
              <button onClick={accept} data-testid={`${testid}-accept`}
                className="flex-1 bg-black text-white py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2">
                <Check className="w-4 h-4" strokeWidth={1.5}/> Use photo
              </button>
            </>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 mt-3 font-mono">Captured photo stays in your browser and is sent only on submit.</p>
      </div>
    </div>
  );
}

function FilePicker({ label, file, onPick, hint, testid, facingMode, sandbox, captureFileStem, qualityContext = 'image' }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [qualityError, setQualityError] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Run quality validation, accept the file only if it passes.
  // Errors stay sticky until the user picks/captures a new file.
  const handlePick = async (f) => {
    setQualityError(null);
    if (!f) { onPick(null); return; }
    setValidating(true);
    const result = await validateImage(f, { sandbox });
    setValidating(false);
    if (!result.ok) {
      setQualityError(result.error);
      onPick(null); // keep the slot empty so Continue stays disabled
      return;
    }
    onPick(f);
  };

  // Feature-detect a real camera (mediaDevices is also exposed on iOS Safari).
  const cameraSupported = typeof navigator !== 'undefined'
    && navigator.mediaDevices?.getUserMedia;

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
             data-testid={testid} onChange={(e)=>handlePick(e.target.files?.[0] || null)}/>
      {preview ? (
        <div className="border border-zinc-200 rounded-sm p-4 flex items-center gap-4" data-testid={`${testid}-preview`}>
          <img src={preview} alt="preview" className="w-20 h-20 object-cover border border-zinc-200 rounded-sm"/>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{file.name}</p>
            <p className="text-xs text-zinc-500 font-mono">{Math.round(file.size/1024)} KB</p>
          </div>
          <button type="button" onClick={() => { setQualityError(null); onPick(null); }} data-testid={`${testid}-clear`}
            className="text-xs text-zinc-500 hover:text-black border border-zinc-200 rounded-sm px-2 py-1">
            Replace
          </button>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm p-5">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-zinc-500 mt-1">{hint}</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {cameraSupported && (
              <button type="button" onClick={() => setCameraOpen(true)} data-testid={`${testid}-camera`}
                className="flex items-center justify-center gap-2 border border-zinc-200 hover:border-zinc-400 rounded-sm py-2.5 text-sm">
                <Camera className="w-4 h-4" strokeWidth={1.5}/> Take photo
              </button>
            )}
            <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload`}
              className={`flex items-center justify-center gap-2 border border-zinc-200 hover:border-zinc-400 rounded-sm py-2.5 text-sm ${cameraSupported ? '' : 'col-span-2'}`}>
              <Upload className="w-4 h-4" strokeWidth={1.5}/> Upload file
            </button>
          </div>
        </div>
      )}
      {validating && (
        <p className="mt-2 text-xs text-zinc-500 font-mono" data-testid={`${testid}-validating`}>Checking image quality…</p>
      )}
      {qualityError && (
        <div className="mt-2 flex items-start gap-2 border border-red-300 bg-red-50 px-3 py-2 rounded-sm text-xs text-red-800"
             data-testid={`${testid}-quality-error`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5}/>
          <span>{qualityError}</span>
        </div>
      )}
      <CameraModal open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handlePick}
        facingMode={facingMode} label={label} testid={testid} captureFileStem={captureFileStem} qualityContext={qualityContext}/>
    </div>
  );
}

const ResultIcon = ({ status }) => {
  const map = {
    approved: { Icon: Check, cls: 'text-green-700 bg-green-50 border-green-200' },
    rejected: { Icon: X, cls: 'text-red-700 bg-red-50 border-red-200' },
    needs_review: { Icon: AlertTriangle, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  };
  const cfg = map[status] || map.needs_review;
  return <span className={`w-12 h-12 grid place-items-center border rounded-sm ${cfg.cls}`}><cfg.Icon className="w-5 h-5" strokeWidth={1.5}/></span>;
};

function onboardingStatusSubtitle(status) {
  if (status === 'approved') return 'Claimed identity matched document evidence and face match passed.';
  if (status === 'rejected') return 'Identity verification could not be approved.';
  if (status === 'needs_review') return 'Identity verification is waiting for review.';
  return 'Identity verification is still in progress.';
}

function collectDeviceSignals() {
  const width = typeof window !== 'undefined' ? window.innerWidth : null;
  const height = typeof window !== 'undefined' ? window.innerHeight : null;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const coarsePointer = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  const touchSupport = typeof navigator !== 'undefined'
    ? navigator.maxTouchPoints > 0 || coarsePointer
    : false;
  const deviceType = /ipad|tablet/i.test(ua)
    ? 'tablet'
    : /mobile|iphone|android/i.test(ua) || (touchSupport && width && width < 900)
      ? 'mobile'
      : 'desktop';
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    viewport_width: width,
    viewport_height: height,
    device_pixel_ratio: window.devicePixelRatio,
    touch_support: touchSupport,
    webdriver: navigator.webdriver === true,
    device_type: deviceType,
    user_agent: ua,
  };
}

export default function PublicVerify() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [step, setStep] = useState(STEP_DETAILS);
  const [docFront, setDocFront] = useState(null);
  const [docBack, setDocBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [docValidating, setDocValidating] = useState(false);
  const [mrzQuality, setMrzQuality] = useState(null);
  const [mrzQualityChecking, setMrzQualityChecking] = useState(false);
  const [claims, setClaims] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    document_country: 'BEL',
    document_type_hint: 'id_card',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorStep, setErrorStep] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [returnSignToken, setReturnSignToken] = useState(null);
  const deviceSignalsSentRef = useRef(false);

  useEffect(() => {
    setReturnSignToken(sessionStorage.getItem('idemgo_return_to_sign'));
    publicApi.get(`/public/sessions/${token}`)
      .then(({ data }) => {
        setSession(data.session);
        setClaims((current) => ({
          first_name: current.first_name || data.session.claimed_first_name || '',
          last_name: current.last_name || data.session.claimed_last_name || '',
          date_of_birth: current.date_of_birth || data.session.claimed_dob || '',
          document_country: current.document_country || data.session.document_country || 'BEL',
          document_type_hint: current.document_type_hint || data.session.document_type_hint || 'id_card',
        }));
        if (!['pending', 'opened'].includes(data.session.status)) setStep(STEP_DONE);
      })
      .catch((e) => {
        const code = e?.response?.data?.error;
        const message = e?.response?.data?.message;
        if (code === 'link_expired' || code === 'link_revoked') {
          setError(message || (code === 'link_expired' ? 'This link has expired.' : 'This link has been revoked and is no longer valid.'));
          return;
        }
        setError('Session not found.');
      });
  }, [token]);

  useEffect(() => {
    if (!session?.device_ip_analysis_enabled || deviceSignalsSentRef.current) return;
    deviceSignalsSentRef.current = true;
    publicApi.post(`/public/sessions/${token}/device-signals`, collectDeviceSignals()).catch(() => {});
  }, [session, token]);

  useEffect(() => {
    let cancelled = false;
    if (!docBack) {
      setMrzQuality(null);
      setMrzQualityChecking(false);
      return () => { cancelled = true; };
    }

    const checkMrzQuality = async () => {
      setMrzQuality(null);
      setMrzQualityChecking(true);
      const fd = new FormData();
      fd.append('id_document_back', docBack);
      try {
        const { data } = await publicApi.post(`/public/sessions/${token}/mrz-quality`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (!cancelled) setMrzQuality(data);
      } catch (e) {
        if (!cancelled) {
          const data = e?.response?.data || {};
          setMrzQuality({
            quality_status: 'bad',
            can_continue: false,
            requires_retry: true,
            user_message: data.message || 'The MRZ strip is too small, blurry, or incomplete. Please retake the back side closer, straight, and without glare.',
            warnings: data.warnings || [data.error || 'mrz_quality_failed'],
          });
        }
      } finally {
        if (!cancelled) setMrzQualityChecking(false);
      }
    };

    void checkMrzQuality();
    return () => { cancelled = true; };
  }, [docBack, token]);

  const submitDocumentFront = async () => {
    setError(null);
    setDocValidating(true);
    const fd = new FormData();
    fd.append('id_document_front', docFront);
    try {
      await publicApi.post(`/public/sessions/${token}/document-front`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStep(STEP_DOC_BACK);
    } catch (e) {
      const data = e?.response?.data || {};
      setError(data.message || 'We could not read the front of your document. Please try again.');
    } finally {
      setDocValidating(false);
    }
  };

  const submitDocumentBack = async () => {
    setError(null);
    if (mrzQuality?.quality_status === 'bad') {
      setError(mrzQuality.user_message || 'Please retake the back side of your document.');
      return;
    }
    setDocValidating(true);
    const fd = new FormData();
    fd.append('id_document_back', docBack);
    try {
      await publicApi.post(`/public/sessions/${token}/document-back`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStep(STEP_SELFIE);
    } catch (e) {
      const data = e?.response?.data || {};
      setError(data.message || 'We could not validate your document. Please try again.');
      setStep(STEP_RETRY_DOC);
    } finally {
      setDocValidating(false);
    }
  };

  const submit = async () => {
    setError(null);
    setErrorStep(null);
    setErrorType(null);
    setSubmitting(true);
    const fd = new FormData();
    fd.append('claimed_first_name', claims.first_name.trim());
    fd.append('claimed_last_name', claims.last_name.trim());
    fd.append('claimed_dob', claims.date_of_birth);
    fd.append('document_country', claims.document_country);
    fd.append('document_type_hint', claims.document_type_hint);
    fd.append('id_document_front', docFront);
    fd.append('id_document_back', docBack);
    fd.append('selfie', selfie);
    try {
      const { data } = await publicApi.post(`/public/sessions/${token}/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSession(data.session);
      setStep(STEP_DONE);
    } catch (e) {
      const data = e?.response?.data || {};
      const responseStep = data.step || 'upload';
      const responseError = data.error || 'unknown_error';
      let msg = data.message || 'Submission failed. Please try again.';

      if (responseError === 'image_too_small') {
        msg = 'Image is too small. Use a photo of at least 600×400 pixels.';
      } else if (responseError === 'image_unreadable') {
        msg = 'We could not read the image. Try another file or format.';
      } else if (responseError === 'missing_files') {
        msg = 'Front side, back side, and selfie photos are required.';
      } else if (responseError === 'ocr_failed') {
        msg = 'We could not process your ID document. The image may be unclear, expired, or in an unsupported format. Please try again with a clearer photo.';
      } else if (responseError === 'document_validation_failed' || responseError === 'mrz_validate_failed') {
        msg = data.message || 'We could not reliably validate your ID card. Please retake clearer photos of both sides of your document.';
      } else if (responseError === 'face_match_failed') {
        msg = 'We could not compare your selfie with your ID document. The selfie image may be unclear or at an unusual angle. Please try again with a clear, front-facing photo.';
      } else if (responseError === 'link_expired') {
        msg = 'This link has expired.';
      } else if (responseError === 'link_revoked') {
        msg = 'This link has been revoked and is no longer valid.';
      }

      setError(msg);
      setErrorStep(responseStep);
      setErrorType(responseError);
    } finally { setSubmitting(false); }
  };

  if (error && !session) {
    const stepLabels = {
      upload: '📤 Upload',
      ocr: '🔍 Checking document…',
      document_validation: '🪪 Checking document…',
      face_match: '👤 Face Comparison'
    };
    const stepLabel = stepLabels[errorStep] || 'KYC verification';
    
    return <div className="min-h-screen grid place-items-center px-6 bg-zinc-50">
      <div className="border border-zinc-200 bg-white p-8 max-w-sm w-full rounded-sm" data-testid="public-error">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" strokeWidth={1.5}/>
          <div className="flex-1">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{stepLabel}</p>
            <p className="text-sm text-red-700 leading-relaxed">{error}</p>
          </div>
        </div>
      </div>
    </div>;
  }
  if (!session) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading…</div>;
  const claimsComplete = claims.first_name.trim() && claims.last_name.trim() && claims.date_of_birth
    && claims.document_country === 'BEL' && claims.document_type_hint === 'id_card';
  const claimedFullName = [session.claimed_first_name, session.claimed_last_name].filter(Boolean).join(' ');
  const isOnboardingIdentity = session.purpose === 'onboarding_identity' && session.is_platform_verification;
  const isIdv = String(session.verification_method || session.method || '').toLowerCase() === 'idv';
  const secureMethodLabel = isIdv ? 'secure IDV verification' : 'secure KYC verification';
  const resultName = session.extracted_name || claimedFullName;
  const stepProgressPct = step === STEP_DETAILS ? (1 / 6) * 100
    : step === STEP_DOC ? (2 / 6) * 100
      : step === STEP_DOC_BACK || step === STEP_RETRY_DOC ? (3 / 6) * 100
        : step === STEP_SELFIE ? (4 / 6) * 100
          : step === STEP_REVIEW ? (5 / 6) * 100
            : 100;
  const stepNumber = step === STEP_DETAILS ? 1
    : step === STEP_DOC ? 2
      : step === STEP_DOC_BACK || step === STEP_RETRY_DOC ? 3
        : step === STEP_SELFIE ? 4
          : step === STEP_REVIEW ? 5
            : 6;

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={25} className="shrink-0"/>
            <span className="font-display text-lg font-bold">idemGO</span>
            <span className="font-mono text-xs text-zinc-400 hidden sm:inline">/ {secureMethodLabel}</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 font-mono"><Lock className="w-3 h-3"/>encrypted</span>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10 sm:py-12">
        <div className="max-w-xl mx-auto">
        {(session.sandbox_mode || session.sandbox) && (
          <div data-testid="public-sandbox-banner"
            className="mb-4 flex items-start gap-2 border border-amber-400 bg-amber-50 px-3 py-2 rounded-sm">
            <span className="text-[10px] uppercase tracking-widest font-mono font-semibold text-amber-800 mt-0.5">Sandbox</span>
            <span className="text-xs text-amber-900">In sandbox, all uploads return DOB 1990-01-01 (age 36) so age-check tests are deterministic.</span>
          </div>
        )}
        <div className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Identity verification</p>
            <p className="text-xs font-mono text-zinc-500">Step {stepNumber} of 6</p>
          </div>
          <div className="mt-3 h-2 bg-zinc-100 rounded-sm overflow-hidden">
            <div className="h-full bg-zinc-900" style={{ width: `${stepProgressPct}%` }}/>
          </div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-8" data-testid="public-card">
          {step === STEP_DETAILS && (
            <>
              <h1 className="font-display text-2xl font-bold">Your details</h1>
              <p className="text-sm text-zinc-600 mt-2">Enter your legal name and date of birth as they appear on your identity document.</p>
              <div className="mt-6 space-y-4">
                <label className="block text-sm">
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">First name</span>
                  <input value={claims.first_name} onChange={(e) => setClaims((current) => ({ ...current, first_name: e.target.value }))}
                    className="mt-2 w-full h-11 border border-zinc-200 rounded-sm px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-zinc-500"
                    autoComplete="given-name"/>
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Last name</span>
                  <input value={claims.last_name} onChange={(e) => setClaims((current) => ({ ...current, last_name: e.target.value }))}
                    className="mt-2 w-full h-11 border border-zinc-200 rounded-sm px-3 py-2 text-base sm:text-sm focus:outline-none focus:border-zinc-500"
                    autoComplete="family-name"/>
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Date of birth</span>
                  <input type="date" value={claims.date_of_birth} onChange={(e) => setClaims((current) => ({ ...current, date_of_birth: e.target.value }))}
                    className="mt-2 w-full h-11 border border-zinc-200 rounded-sm px-3 py-2 text-base sm:text-sm leading-none appearance-none focus:outline-none focus:border-zinc-500"
                    autoComplete="bday"/>
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Document country</span>
                  <select value={claims.document_country} onChange={(e) => setClaims((current) => ({ ...current, document_country: e.target.value }))}
                    className="mt-2 w-full h-11 border border-zinc-200 rounded-sm px-3 py-2 text-base sm:text-sm bg-white focus:outline-none focus:border-zinc-500">
                    <option value="BEL">Belgium</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Document type</span>
                  <select value={claims.document_type_hint} onChange={(e) => setClaims((current) => ({ ...current, document_type_hint: e.target.value }))}
                    className="mt-2 w-full h-11 border border-zinc-200 rounded-sm px-3 py-2 text-base sm:text-sm bg-white focus:outline-none focus:border-zinc-500">
                    <option value="id_card">National ID card</option>
                  </select>
                </label>
              </div>
              <p className="text-xs text-zinc-500 mt-3">Currently only Belgian ID cards are supported.</p>
              <button data-testid="step-details-next" onClick={() => setStep(STEP_DOC)} disabled={!claimsComplete}
                className="w-full mt-6 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                Continue
              </button>
            </>
          )}

          {step === STEP_DOC && (
            <>
              <h1 className="font-display text-2xl font-bold">Front side</h1>
              <p className="text-sm text-zinc-600 mt-2">Take a clear photo of the front of your Belgian ID card.</p>
              {session.min_age > 0 && (
                <div data-testid="public-age-notice"
                  className="mt-4 border border-zinc-300 bg-zinc-50 px-3 py-2 rounded-sm flex items-start gap-2 text-xs text-zinc-700">
                  <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5}/>
                  <span>You must be <strong>{session.min_age}+</strong> to verify with {session.organization_name || 'this service'}.</span>
                </div>
              )}
              <div className="mt-6">
                <FilePicker label="Front side" hint="JPG, PNG or WEBP · max 8 MB · min 600×400"
                  file={docFront} onPick={setDocFront} testid="upload-id-front" facingMode="environment"
                  captureFileStem="id_document_front"
                  sandbox={!!(session.sandbox_mode || session.sandbox)}/>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setStep(STEP_DETAILS)} className="flex-1 border border-zinc-200 py-3 rounded-sm text-sm">Back</button>
                <button data-testid="step-doc-next" onClick={() => void submitDocumentFront()} disabled={!docFront || docValidating}
                  className="flex-1 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                  {docValidating ? 'Checking document…' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {step === STEP_DOC_BACK && (
            <>
              <h1 className="font-display text-2xl font-bold">Back side</h1>
              <p className="text-sm text-zinc-600 mt-2">Photograph the back of the same card so we can read the coded strip.</p>
              <div className="mt-6">
                <FilePicker label="Back side" hint="JPG, PNG or WEBP · max 8 MB · min 600×400"
                  file={docBack} onPick={setDocBack} testid="upload-id-back" facingMode="environment"
                  captureFileStem="id_document_back"
                  qualityContext="mrz"
                  sandbox={!!(session.sandbox_mode || session.sandbox)}/>
              </div>
              {mrzQualityChecking && (
                <p className="mt-3 text-xs text-zinc-500 font-mono" data-testid="mrz-quality-checking">Checking MRZ strip…</p>
              )}
              {mrzQuality && (
                <div
                  data-testid="mrz-quality-result"
                  className={`mt-3 flex items-start gap-2 border px-3 py-2 rounded-sm text-xs ${
                    mrzQuality.quality_status === 'good'
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                      : mrzQuality.quality_status === 'warning'
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-red-300 bg-red-50 text-red-800'
                  }`}
                >
                  {mrzQuality.quality_status === 'good'
                    ? <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5}/>
                    : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5}/>}
                  <span>{mrzQuality.user_message}</span>
                </div>
              )}
              {docValidating && (
                <p className="mt-4 text-sm text-zinc-600" data-testid="doc-back-validating">Validating document information…</p>
              )}
              <div className="flex gap-2 mt-6">
                <button onClick={() => setStep(STEP_DOC)} className="flex-1 border border-zinc-200 py-3 rounded-sm text-sm">Back</button>
                <button data-testid="step-doc-back-next" onClick={() => void submitDocumentBack()} disabled={!docBack || docValidating || mrzQualityChecking || mrzQuality?.quality_status === 'bad'}
                  className="flex-1 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                  {docValidating ? 'Validating document information…' : mrzQualityChecking ? 'Checking MRZ…' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {step === STEP_RETRY_DOC && (
            <>
              <h1 className="font-display text-2xl font-bold">We need clearer photos</h1>
              <p className="text-sm text-zinc-600 mt-2">{error || 'Please retake clearer photos of both sides of your document.'}</p>
              <div className="flex gap-2 mt-6">
                <button
                  data-testid="retry-doc-both"
                  onClick={() => {
                    setError(null);
                    setDocFront(null);
                    setDocBack(null);
                    setStep(STEP_DOC);
                  }}
                  className="flex-1 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800">
                  Retake both sides
                </button>
              </div>
            </>
          )}

          {step === STEP_SELFIE && (
            <>
              <h1 className="font-display text-2xl font-bold">Take a selfie</h1>
              <p className="text-sm text-zinc-600 mt-2">A clear, well-lit photo of your face. We'll match it against your ID.</p>
              <div className="mt-6">
                <FilePicker label="Upload selfie" hint="JPG, PNG or WEBP · max 8 MB · min 600×400"
                  file={selfie} onPick={setSelfie} testid="upload-selfie" facingMode="user"
                  sandbox={!!(session.sandbox_mode || session.sandbox)}/>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setStep(STEP_DOC_BACK)} className="flex-1 border border-zinc-200 py-3 rounded-sm text-sm">Back</button>
                <button data-testid="step-selfie-next" onClick={() => setStep(STEP_REVIEW)} disabled={!selfie}
                  className="flex-1 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                  Continue
                </button>
              </div>
            </>
          )}

          {step === STEP_REVIEW && (
            <>
              <h1 className="font-display text-2xl font-bold">Review &amp; submit</h1>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between border-b border-zinc-100 py-2 gap-4"><span className="text-zinc-500">Claimed name</span><span className="font-mono text-xs text-right">{claims.first_name.trim()} {claims.last_name.trim()}</span></div>
                <div className="flex justify-between border-b border-zinc-100 py-2 gap-4"><span className="text-zinc-500">Claimed DOB</span><span className="font-mono text-xs text-right">{claims.date_of_birth}</span></div>
                <div className="flex justify-between border-b border-zinc-100 py-2"><span className="text-zinc-500">Front side</span><span className="font-mono text-xs">{docFront?.name}</span></div>
                <div className="flex justify-between border-b border-zinc-100 py-2"><span className="text-zinc-500">Back side</span><span className="font-mono text-xs">{docBack?.name}</span></div>
                <div className="flex justify-between border-b border-zinc-100 py-2"><span className="text-zinc-500">Selfie</span><span className="font-mono text-xs">{selfie?.name}</span></div>
              </div>
              <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
                <Camera className="w-3 h-3 inline mr-1" strokeWidth={1.5}/>
                Images are processed in memory only. They are never stored on idemGO servers.
              </p>
              {error && <p className="text-sm text-red-700 mt-3" data-testid="public-submit-error">{error}</p>}
              <div className="flex gap-2 mt-6">
                <button onClick={() => setStep(STEP_SELFIE)} disabled={submitting}
                  className="flex-1 border border-zinc-200 py-3 rounded-sm text-sm disabled:opacity-50">Back</button>
                <button onClick={submit} disabled={submitting} data-testid="public-submit"
                  className="flex-1 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
                  {submitting ? 'Verifying…' : 'Submit'}
                </button>
              </div>
            </>
          )}

          {step === STEP_DONE && (
            <div className="text-center" data-testid="public-result">
              <div className="grid place-items-center mb-4"><ResultIcon status={session.status}/></div>
              <h1 className="font-display text-2xl font-bold capitalize">{session.status?.replace('_', ' ')}</h1>
              {isOnboardingIdentity ? (
                <p className="text-sm text-zinc-600 mt-2">{onboardingStatusSubtitle(session.status)}</p>
              ) : session.reason && <p className="text-sm text-zinc-600 mt-2">{session.reason}</p>}
              {isOnboardingIdentity ? (
                <div className="mt-6 text-left text-sm border border-zinc-200 rounded-sm p-4 bg-zinc-50">
                  {claimedFullName && (
                    <div className="flex justify-between py-1 gap-4">
                      <span className="text-zinc-500">Claimed name</span>
                      <span className="font-mono text-right">{claimedFullName}</span>
                    </div>
                  )}
                  {session.claimed_dob && (
                    <div className="flex justify-between py-1 gap-4">
                      <span className="text-zinc-500">Claimed DOB</span>
                      <span className="font-mono text-right">{session.claimed_dob}</span>
                    </div>
                  )}
                  {resultName && (
                    <div className="flex justify-between py-1 gap-4">
                      <span className="text-zinc-500">Verified name</span>
                      <span className="font-mono text-right">{resultName}</span>
                    </div>
                  )}
                  {session.extracted_dob && (
                    <div className="flex justify-between py-1 gap-4">
                      <span className="text-zinc-500">Verified DOB</span>
                      <span className="font-mono text-right">{session.extracted_dob}</span>
                    </div>
                  )}
                </div>
              ) : (session.extracted_name || claimedFullName) && (
                <div className="mt-6 text-left text-sm border border-zinc-200 rounded-sm p-4 bg-zinc-50">
                  {claimedFullName && <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Claimed name</span><span className="font-mono text-right">{claimedFullName}</span></div>}
                  {session.claimed_dob && <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Claimed DOB</span><span className="font-mono text-right">{session.claimed_dob}</span></div>}
                  {session.extracted_name && <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">OCR name</span><span className="font-mono text-right">{session.extracted_name}</span></div>}
                  {session.extracted_dob && <div className="flex justify-between py-1"><span className="text-zinc-500">DOB</span><span className="font-mono">{session.extracted_dob}</span></div>}
                  {session.extracted_document_number && <div className="flex justify-between py-1"><span className="text-zinc-500">Document #</span><span className="font-mono">{session.extracted_document_number}</span></div>}
                </div>
              )}
              {session.status === 'approved' && returnSignToken && (
                <button onClick={() => {
                  sessionStorage.removeItem('idemgo_return_to_sign');
                  window.location.assign(`/sign/${returnSignToken}`);
                }}
                  className="w-full mt-6 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800">
                  Continue to document
                </button>
              )}
              <p className="text-xs text-zinc-500 mt-6 font-mono">You can close this window.</p>
            </div>
          )}
        </div>
        </div>
      </main>
    </div>
  );
}
